import { useState, useEffect, useCallback } from 'react'
import {
  agentsApi,
  departmentsApi,
  userCategoriesApi,
  type AgentDetail,
  type AgentProfile,
  type AgentRequestBody,
  type Department,
  type UserCategory,
} from '../api/team'
import { userDirectoryApi, type DirectoryUser } from '../api/userDirectory'
import { usersApi, type CrmUser } from '../api/users'
import {
  Page,
  PageHeader,
  FilterBar,
  Button,
  IconButton,
  AccessDenied,
  Callout,
  LoadingState,
  EmptyState,
  Card,
  DataTable,
  StatusPill,
  Dialog,
  TextField,
  Select,
  Pagination,
  useSnackbar,
  useConfirm,
  FormSection,
  SegmentedButton,
  Switch,
} from '../components/ui'

type ActiveFilter = 'all' | 'active' | 'inactive'

/** Ruoli proposti per l'accesso di un nuovo agente; il primo è quello predefinito. */
const AGENT_ROLES: { value: string; label: string }[] = [
  { value: 'EqualAgente', label: 'Agente' },
  { value: 'EqualAgenzia', label: 'Agenzia' },
  { value: 'EqualSegnalatore', label: 'Segnalatore' },
  { value: 'EqualOperatore', label: 'Operatore' },
]

const MIN_PASSWORD = 8

/** Una mail utilizzabile per entrare: vuota o senza «@» vale come mancante. */
function hasEmail(email?: string | null): boolean {
  return !!email && email.includes('@')
}

interface AgentFormState {
  /** Nuovo agente: crea anche l'accesso al CRM (nome, mail, password) o collega un utente che esiste già. */
  accessMode: 'new' | 'existing'
  accName: string
  accEmail: string
  /** Nuovo accesso: obbligatoria. Agente esistente: vuota = la password resta quella di prima. */
  accPassword: string
  accRole: string
  /** L'utente può entrare nel CRM; false = account disattivato (il login risponde «account disattivato»). */
  accActive: boolean
  userId: number | ''
  agencyId: number | ''
  departmentId: number | ''
  categoryId: number | ''
  code: string
  vatNumber: string
  fiscalCode: string
  iban: string
  commissionPercentage: number | ''
  notes: string
}

const EMPTY_AGENT_FORM: AgentFormState = {
  accessMode: 'new',
  accName: '',
  accEmail: '',
  accPassword: '',
  accRole: 'EqualAgente',
  accActive: true,
  userId: '',
  agencyId: '',
  departmentId: '',
  categoryId: '',
  code: '',
  vatNumber: '',
  fiscalCode: '',
  iban: '',
  commissionPercentage: '',
  notes: '',
}

export function TeamPage() {
  const askConfirm = useConfirm()
  const snackbar = useSnackbar()
  const [items, setItems] = useState<AgentProfile[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [unauthorized, setUnauthorized] = useState(false)

  const [departments, setDepartments] = useState<Department[]>([])
  const [deptFilter, setDeptFilter] = useState<number | undefined>(undefined)
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>('all')

  // TASK-0072: users + categories for the create/edit form's selectors — fetched once on
  // mount, same fetch-once/silently-swallow-errors pattern already used elsewhere in this
  // session (e.g. PipelinesPage, TASK-0067) rather than duplicating the fetch logic. No role
  // filter on usersApi.list(): this page covers both "Agenti & Operatori", and AgentProfile.UserId
  // has no role restriction on the backend (plain long, no FK to a specific role).
  const [users, setUsers] = useState<DirectoryUser[]>([])
  const [categories, setCategories] = useState<UserCategory[]>([])
  // Accessi (nome e mail) per la colonna Agente: /api/users è solo per gli Admin, per gli altri resta il nome
  // dall'elenco utenti.
  const [accounts, setAccounts] = useState<Map<number, CrmUser>>(new Map())
  // Utente collegato all'agente in modifica, letto per intero: la PUT degli utenti sostituisce tutti i campi.
  const [linkedUser, setLinkedUser] = useState<CrmUser | null>(null)
  // Accesso creato da un salvataggio non andato a buon fine: il tentativo successivo lo riusa invece di
  // crearne un secondo con la stessa mail.
  const [createdUserId, setCreatedUserId] = useState<number | null>(null)

  const [showModal, setShowModal] = useState(false)
  const [editId, setEditId] = useState<number | null>(null)
  const [form, setForm] = useState<AgentFormState>(EMPTY_AGENT_FORM)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [info, setInfo] = useState('')
  const [loadingDetail, setLoadingDetail] = useState(false)

  const pageSize = 25

  // Load department list once for the filter select
  useEffect(() => {
    departmentsApi
      .list()
      .then(res => setDepartments(res))
      .catch(() => {
        // non-critical — silently ignore
      })
  }, [])

  const loadUsers = useCallback(() => {
    userDirectoryApi.list({ activeOnly: false, limit: 200 }).then(setUsers).catch(() => setUsers([]))
    usersApi.list({ size: 200 })
      .then(res => setAccounts(new Map(res.items.map(u => [u.id, u]))))
      .catch(() => setAccounts(new Map()))
  }, [])

  // Load users + categories once for the create/edit form's selectors
  useEffect(() => {
    loadUsers()
    userCategoriesApi.list().then(setCategories).catch(() => setCategories([]))
  }, [loadUsers])

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    setUnauthorized(false)
    try {
      const isActiveParam =
        activeFilter === 'active' ? true : activeFilter === 'inactive' ? false : undefined
      const p: { page: number; departmentId?: number; isActive?: boolean } = { page }
      if (deptFilter !== undefined) p.departmentId = deptFilter
      if (isActiveParam !== undefined) p.isActive = isActiveParam
      const res = await agentsApi.list(p)
      setItems(res.items)
      setTotal(res.total)
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Errore durante il caricamento'
      if (msg.includes('403') || msg.toLowerCase().includes('forbidden') || msg.toLowerCase().includes('autorizzat')) {
        setUnauthorized(true)
      } else {
        setError(msg)
      }
    } finally {
      setLoading(false)
    }
  }, [deptFilter, activeFilter, page])

  useEffect(() => { void load() }, [load])

  async function handleDelete(agent: AgentProfile) {
    if (!(await askConfirm({ title: 'Archivia profilo agente', message: `Archiviare il profilo agente con ID ${agent.id} (user #${agent.userId})? Il profilo viene disattivato e resta in archivio.`, confirmLabel: 'Archivia', destructive: true }))) return
    try {
      const res = await agentsApi.delete(agent.id)
      if (res?.archived) snackbar.show({ message: 'Profilo archiviato (disattivato). Compare tra i non attivi.', tone: 'success' })
      void load()
    } catch (e) {
      snackbar.show({ message: e instanceof Error ? e.message : "Errore durante l'archiviazione", tone: 'error' })
    }
  }

  function deptName(id?: number): string {
    if (!id) return '—'
    const d = departments.find(x => x.id === id)
    return d ? d.name : `#${id}`
  }

  function agentName(userId: number): string {
    return accounts.get(userId)?.name ?? users.find(u => u.id === userId)?.name ?? `Utente #${userId}`
  }

  function openCreate() {
    setForm(EMPTY_AGENT_FORM)
    setEditId(null)
    setLinkedUser(null)
    setCreatedUserId(null)
    setSaveError('')
    setInfo('')
    setShowModal(true)
  }

  // TASK-0072: fetches the single-record detail (agentsApi.get), not the list item — List
  // deliberately excludes VatNumber/FiscalCode/Iban/CommissionPercentage/Notes (RSK-009/
  // TASK-0010/TASK-0012), but Update is a full-replace PUT (AgentProfile.Update overwrites
  // every field, including with null). Prefilling from the list item alone would silently
  // blank those fields out on save even though the UI never touched them.
  /** Riempie la finestra con un agente esistente e il suo utente (null se non si può leggere). */
  function fillEdit(detail: AgentDetail, account: CrmUser | null) {
    setLinkedUser(account)
    setForm({
      accessMode: 'existing',
      accName: account?.name ?? '',
      accEmail: hasEmail(account?.email) ? account!.email : '',
      accPassword: '',
      accRole: account?.role ?? 'EqualAgente',
      accActive: account?.isActive ?? true,
      userId: detail.userId,
      agencyId: detail.agencyId ?? '',
      departmentId: detail.departmentId ?? '',
      categoryId: detail.categoryId ?? '',
      code: detail.code ?? '',
      vatNumber: detail.vatNumber ?? '',
      fiscalCode: detail.fiscalCode ?? '',
      iban: detail.iban ?? '',
      commissionPercentage: detail.commissionPercentage ?? '',
      notes: detail.notes ?? '',
    })
    setEditId(detail.id)
  }

  /**
   * Nuovo agente da un utente esistente: carica l'utente per mostrarne nome e mail (e poterli completare). Se l'utente
   * è già agente, la finestra passa alla modifica del suo profilo: un secondo profilo sullo stesso utente non è ammesso.
   */
  async function pickExistingUser(userId: number | '') {
    setSaveError('')
    setInfo('')
    if (userId === '') {
      setLinkedUser(null)
      setForm(f => ({ ...f, userId: '', accName: '', accEmail: '', accPassword: '', accActive: true }))
      return
    }
    setLoadingDetail(true)
    try {
      const [account, existing] = await Promise.all([
        usersApi.get(userId).catch(() => null),
        agentsApi.getByUser(userId),
      ])
      if (existing) {
        fillEdit(existing, account)
        setInfo(`${account?.name ?? 'Questo utente'} è già un agente: qui puoi completarne mail, password e dati.`)
        return
      }
      setLinkedUser(account)
      setForm(f => ({
        ...f,
        userId,
        accName: account?.name ?? '',
        accEmail: hasEmail(account?.email) ? account!.email : '',
        accPassword: '',
        accActive: account?.isActive ?? true,
      }))
    } finally {
      setLoadingDetail(false)
    }
  }

  async function openEdit(agent: AgentProfile) {
    setSaveError('')
    setInfo('')
    setLoadingDetail(true)
    try {
      const detail = await agentsApi.get(agent.id)
      const account = await usersApi.get(detail.userId).catch(() => null)
      fillEdit(detail, account)
      setShowModal(true)
    } catch (e) {
      snackbar.show({ message: e instanceof Error ? e.message : "Errore durante il caricamento dell'agente", tone: 'error' })
    } finally {
      setLoadingDetail(false)
    }
  }

  function closeModal() {
    setShowModal(false)
    setEditId(null)
    setLinkedUser(null)
    setCreatedUserId(null)
    setSaveError('')
    setInfo('')
  }

  /** In modifica: true se nome, mail o password dell'utente collegato sono stati toccati. */
  function accessTouched(): boolean {
    if (!linkedUser) return false
    const emailBefore = hasEmail(linkedUser.email) ? linkedUser.email : ''
    return form.accName.trim() !== linkedUser.name
      || form.accEmail.trim().toLowerCase() !== emailBefore
      || form.accPassword !== ''
      || form.accActive !== linkedUser.isActive
  }

  /** Controlla i campi dell'accesso; ritorna il messaggio d'errore o null. */
  function accessError(): string | null {
    const creating = editId === null && form.accessMode === 'new'
    if (!creating && editId === null && form.userId === '') return 'Scegli l’utente da collegare'
    // Utente collegato (agente in modifica o utente esistente scelto): nome, mail e password si possono completare.
    const editingAccess = !creating && linkedUser !== null
    if (!creating && !editingAccess) return null
    // Agente che non ha ancora un accesso e a cui non si aggiunge: si salvano solo i dati dell'agente.
    if (editingAccess && !accessTouched()) return null
    if (!form.accName.trim()) return 'Il nome è obbligatorio'
    if (!hasEmail(form.accEmail.trim())) return 'Serve una mail valida: è quella con cui l’agente entra nel CRM'
    if (creating && !form.accPassword) return 'La password è obbligatoria per il nuovo accesso'
    if (form.accPassword && form.accPassword.length < MIN_PASSWORD) return `La password deve avere almeno ${MIN_PASSWORD} caratteri`
    return null
  }

  /** Crea l'accesso (nuovo agente) o aggiorna nome, mail e password dell'utente collegato; ritorna l'id utente. */
  async function saveAccess(): Promise<number> {
    const name = form.accName.trim()
    const email = form.accEmail.trim()
    if (editId === null) {
      if (form.accessMode === 'new') {
        if (createdUserId !== null) return createdUserId
        const { id } = await usersApi.create({ name, email, password: form.accPassword, role: form.accRole })
        setCreatedUserId(id)
        return id
      }
    }
    if (linkedUser && accessTouched()) {
      if (name !== linkedUser.name || email.toLowerCase() !== linkedUser.email || form.accActive !== linkedUser.isActive) {
        await usersApi.update(linkedUser.id, {
          name, email, role: linkedUser.role, isActive: form.accActive,
          userCategoryId: linkedUser.userCategoryId, parentUserId: linkedUser.parentUserId,
        })
      }
      if (form.accPassword) await usersApi.resetPassword(linkedUser.id, form.accPassword)
    }
    return form.userId as number
  }

  async function handleSave() {
    const invalid = accessError()
    if (invalid) {
      setSaveError(invalid)
      return
    }
    setSaving(true)
    setSaveError('')
    try {
      const userId = await saveAccess()
      const payload: AgentRequestBody = { userId }
      if (form.agencyId !== '') payload.agencyId = form.agencyId
      if (form.departmentId !== '') payload.departmentId = form.departmentId
      if (form.categoryId !== '') payload.categoryId = form.categoryId
      if (form.code.trim()) payload.code = form.code.trim()
      if (form.vatNumber.trim()) payload.vatNumber = form.vatNumber.trim()
      if (form.fiscalCode.trim()) payload.fiscalCode = form.fiscalCode.trim()
      if (form.iban.trim()) payload.iban = form.iban.trim()
      if (form.commissionPercentage !== '') payload.commissionPercentage = form.commissionPercentage
      if (form.notes.trim()) payload.notes = form.notes.trim()
      if (editId !== null) {
        await agentsApi.update(editId, payload)
      } else {
        await agentsApi.create(payload)
      }
      snackbar.show({
        message: editId !== null
          ? 'Agente salvato.'
          : form.accessMode === 'new'
            ? `Agente creato: entra nel CRM con ${form.accEmail.trim().toLowerCase()} e la password scelta.`
            : 'Agente creato.',
        tone: 'success',
      })
      closeModal()
      loadUsers()
      void load()
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Errore durante il salvataggio')
    } finally {
      setSaving(false)
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  return (
    <Page>
      <PageHeader
        title="Utenti e agenti"
        icon="badge"
        count={total > 0 ? total : undefined}
        actions={
          !unauthorized ? (
            <Button variant="filled" icon="add" onClick={openCreate} disabled={loadingDetail}>
              Nuovo agente
            </Button>
          ) : undefined
        }
      />

      {unauthorized && <AccessDenied requirement="il ruolo EqualStaff" />}

      {error && <Callout tone="error">{error}</Callout>}

      {!unauthorized && (
        <>
          <FilterBar>
            <Select
              label="Reparto"
              value={deptFilter ?? ''}
              onChange={e => {
                const val = e.target.value
                setDeptFilter(val ? parseInt(val, 10) : undefined)
                setPage(1)
              }}
            >
              <option value="">Tutti i reparti</option>
              {departments.map(d => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </Select>
            <Select
              label="Stato"
              value={activeFilter}
              onChange={e => {
                setActiveFilter(e.target.value as ActiveFilter)
                setPage(1)
              }}
            >
              <option value="all">Tutti</option>
              <option value="active">Attivi</option>
              <option value="inactive">Inattivi</option>
            </Select>
          </FilterBar>

          <Card variant="outlined" padding="none">
            {loading ? (
              <LoadingState />
            ) : items.length === 0 ? (
              <EmptyState icon="badge" title="Nessun agente trovato" />
            ) : (
              <DataTable variant="plain">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Agente</th>
                    <th>Reparto</th>
                    <th>Codice</th>
                    <th>Stato</th>
                    <th className="text-end">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(a => (
                    <tr key={a.id}>
                      <td className="text-muted md-num">#{a.id}</td>
                      <td>
                        <div>{agentName(a.userId)}</div>
                        {accounts.has(a.userId) && (
                          hasEmail(accounts.get(a.userId)!.email)
                            ? <div className="text-muted md-typescale-body-small">{accounts.get(a.userId)!.email}</div>
                            : <StatusPill tone="warning">Senza mail: non può entrare</StatusPill>
                        )}
                        {accounts.get(a.userId)?.isActive === false && (
                          <StatusPill tone="warning">Accesso disattivato</StatusPill>
                        )}
                      </td>
                      <td>{deptName(a.departmentId)}</td>
                      <td>
                        {a.code ? (
                          <code className="md-typescale-body-small">{a.code}</code>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td>
                        {a.isActive ? (
                          <StatusPill tone="success">Attivo</StatusPill>
                        ) : (
                          <StatusPill tone="neutral">Inattivo</StatusPill>
                        )}
                      </td>
                      <td className="text-end">
                        <div className="d-flex gap-1 justify-content-end">
                          <IconButton
                            icon="edit"
                            size="sm"
                            aria-label="Modifica"
                            disabled={loadingDetail}
                            onClick={() => void openEdit(a)}
                          />
                          <IconButton
                            icon="delete"
                            size="sm"
                            aria-label="Archivia"
                            onClick={() => void handleDelete(a)}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
            )}
            {totalPages > 1 && <Pagination page={page} pageSize={pageSize} total={total} onChange={setPage} />}
          </Card>
        </>
      )}

      <Dialog as="form"
 onSubmit={(e) => { e.preventDefault(); void handleSave() }}
        open={showModal}
        onClose={closeModal}
        title={editId !== null ? 'Modifica agente' : 'Nuovo agente'}
        actions={
          <>
            <Button variant="text" onClick={closeModal}>
              Annulla
            </Button>
            <Button type="submit" variant="text" disabled={saving} >
              {saving ? 'Salvataggio...' : 'Salva'}
            </Button>
          </>
        }
      >
        <>
          {saveError && <Callout tone="error">{saveError}</Callout>}
          {info && <Callout tone="info">{info}</Callout>}
          <FormSection title="Accesso al CRM">
            {editId === null && (
              <div className="md-span-full">
                <SegmentedButton
                  aria-label="Accesso dell'agente"
                  value={form.accessMode}
                  onChange={v => {
                    setLinkedUser(null)
                    setForm(f => ({ ...f, accessMode: v as AgentFormState['accessMode'], userId: '', accName: '', accEmail: '', accPassword: '', accActive: true }))
                  }}
                  options={[
                    { value: 'new', label: 'Crea un nuovo accesso' },
                    { value: 'existing', label: 'Utente già esistente' },
                  ]}
                />
              </div>
            )}

            {editId === null && form.accessMode === 'existing' && (
              <Select
                className="md-span-full"
                label="Utente *"
                value={form.userId}
                onChange={e => {
                  const val = e.target.value
                  void pickExistingUser(val ? parseInt(val, 10) : '')
                }}
              >
                <option value="">Seleziona un utente…</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </Select>
            )}

            {form.userId !== '' && form.accessMode === 'existing' && !linkedUser && !loadingDetail && (
              <Callout tone="info" className="md-span-full">
                Utente collegato: {agentName(form.userId as number)}. Mail e password le può cambiare solo un amministratore.
              </Callout>
            )}
            {form.accessMode === 'existing' && linkedUser && !hasEmail(linkedUser.email) && (
              <Callout tone="warning" className="md-span-full">
                {linkedUser.name} non ha una mail: senza mail e password non può entrare nel CRM. Inseriscile qui sotto.
              </Callout>
            )}
            {linkedUser && !form.accActive && (
              <Callout tone="warning" className="md-span-full">
                L’accesso di {linkedUser.name} è disattivato: anche con mail e password giuste non può entrare. Attiva «Può entrare nel CRM» qui sotto.
              </Callout>
            )}

            {(form.accessMode === 'new' || linkedUser) && (
              <>
                <TextField
                  label="Nome e cognome *"
                  type="text"
                  autoComplete="off"
                  value={form.accName}
                  onChange={e => setForm(f => ({ ...f, accName: e.target.value }))}
                />
                <TextField
                  label="Mail *"
                  type="email"
                  autoComplete="off"
                  supportingText="La usa per entrare nel CRM"
                  value={form.accEmail}
                  onChange={e => setForm(f => ({ ...f, accEmail: e.target.value }))}
                />
                <TextField
                  label={form.accessMode === 'new' ? 'Password *' : 'Nuova password'}
                  type="password"
                  autoComplete="new-password"
                  supportingText={form.accessMode === 'new'
                    ? `Almeno ${MIN_PASSWORD} caratteri`
                    : `Lascia vuoto per non cambiarla. Almeno ${MIN_PASSWORD} caratteri`}
                  value={form.accPassword}
                  onChange={e => setForm(f => ({ ...f, accPassword: e.target.value }))}
                />
                {linkedUser && (
                  <Switch
                    id="agent-acc-active"
                    label="Può entrare nel CRM"
                    checked={form.accActive}
                    onChange={e => setForm(f => ({ ...f, accActive: e.target.checked }))}
                  />
                )}
                {form.accessMode === 'new' && (
                  <Select
                    label="Ruolo"
                    value={form.accRole}
                    onChange={e => setForm(f => ({ ...f, accRole: e.target.value }))}
                  >
                    {AGENT_ROLES.map(r => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </Select>
                )}
              </>
            )}
          </FormSection>

          <FormSection title="Agente">
            <TextField
              label="Codice"
              type="text"
              supportingText="Codice agente"
              value={form.code}
              onChange={e => setForm(f => ({ ...f, code: e.target.value }))}
            />
            <TextField
              label="Agenzia (ID)"
              type="number"
              min={0}
              value={form.agencyId}
              onChange={e =>
                setForm(f => ({ ...f, agencyId: e.target.value ? parseInt(e.target.value, 10) : '' }))
              }
            />
            <Select
              label="Reparto"
              value={form.departmentId}
              onChange={e => {
                const val = e.target.value
                setForm(f => ({ ...f, departmentId: val ? parseInt(val, 10) : '' }))
              }}
            >
              <option value="">—</option>
              {departments.map(d => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </Select>
            <Select
              label="Categoria"
              value={form.categoryId}
              onChange={e => {
                const val = e.target.value
                setForm(f => ({ ...f, categoryId: val ? parseInt(val, 10) : '' }))
              }}
            >
              <option value="">—</option>
              {categories.map(c => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </FormSection>

          <FormSection title="Dati riservati (visibili solo agli Admin)">
            <TextField
              label="Partita IVA"
              type="text"
              value={form.vatNumber}
              onChange={e => setForm(f => ({ ...f, vatNumber: e.target.value }))}
            />
            <TextField
              label="Codice Fiscale"
              type="text"
              value={form.fiscalCode}
              onChange={e => setForm(f => ({ ...f, fiscalCode: e.target.value }))}
            />
            <TextField
              label="IBAN"
              type="text"
              value={form.iban}
              onChange={e => setForm(f => ({ ...f, iban: e.target.value }))}
            />
            <TextField
              label="Percentuale Commissione"
              type="number"
              min={0}
              max={100}
              step={0.01}
              suffix="%"
              value={form.commissionPercentage}
              onChange={e =>
                setForm(f => ({
                  ...f,
                  commissionPercentage: e.target.value ? parseFloat(e.target.value) : '',
                }))
              }
            />
            <TextField
              className="md-span-full"
              label="Note"
              multiline
              rows={2}
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
            />
          </FormSection>
        </>
      </Dialog>
    </Page>
  )
}
